/**
 * Evidence-gathering for an iPhone-only bug report (PWA, standalone): the
 * chapter illustration's full-screen view sometimes freezes the whole app
 * and stops audio when pinching back OUT (dezoom), as if the page
 * reloaded. Not reproducible in this environment — see
 * `ChapterSceneFullscreen.tsx` for the defensive rendering/gesture-
 * handling changes that go alongside this; this module is only the "did a
 * reload actually happen mid-gesture" detector plus the breadcrumb trail
 * that corroborates it either way.
 *
 * The detector: sessionStorage survives a same-tab reload (or a WebContent
 * process getting killed and iOS silently relaunching the page — from
 * script, that looks identical to a reload) but is cleared once the tab
 * itself is actually closed. So a flag set the moment the view opens, and
 * only ever cleared by a clean close, tells the NEXT boot whether the
 * previous one ended while the view was still open — which a real user
 * navigating away, or backgrounding then quitting the app, would never
 * leave behind.
 */
import { addBreadcrumb, captureMessage } from "./sentry";

const OPEN_FLAG_KEY = "chouette.chapterSceneFullscreen.openAtPagehide";

/** Pure: whether the raw sessionStorage value means "still open at the
 *  end of the previous session" — split out so it's testable without a
 *  real `sessionStorage`. */
export function wasOpenAtLastPagehide(rawFlag: string | null): boolean {
  return rawFlag === "1";
}

function setSessionFlag(open: boolean): void {
  try {
    if (open) sessionStorage.setItem(OPEN_FLAG_KEY, "1");
    else sessionStorage.removeItem(OPEN_FLAG_KEY);
  } catch {
    /* private browsing / storage unavailable — evidence-gathering only, never fatal */
  }
}

function readSessionFlag(): string | null {
  try {
    return sessionStorage.getItem(OPEN_FLAG_KEY);
  } catch {
    return null;
  }
}

/** Called once at app boot (main.tsx), before anything else touches the
 *  flag. If the previous session left it set, something ended the page
 *  while the full-screen view was open without going through its own
 *  clean-close path — most plausibly the reload-after-crash this is
 *  hunting for. Clears the flag either way so a genuine future occurrence
 *  isn't masked by a stale one. */
export function reportIfReloadedWhileFullscreenOpen(): void {
  if (!wasOpenAtLastPagehide(readSessionFlag())) return;
  captureMessage("chapter fullscreen: page reloaded while open", { level: "warning" });
  setSessionFlag(false);
}

/** Marks the view as open for the detector above, and wires the
 *  pagehide/visibilitychange breadcrumbs asked for as corroborating
 *  evidence either way. Call once on mount; the returned cleanup removes
 *  the listeners AND clears the flag — a normal unmount is a clean close,
 *  the one thing that must NOT look like a crash to the next boot. */
export function trackFullscreenLifecycle(): () => void {
  setSessionFlag(true);
  addBreadcrumb("chapter fullscreen: opened", { category: "chapter-scene" });
  function onPageHide() {
    // Not itself proof of a crash — an ordinary backgrounding/close fires
    // this too. It's only suspicious in hindsight, if the flag it leaves
    // behind is still there at the next boot (reportIfReloadedWhileFullscreenOpen).
    addBreadcrumb("chapter fullscreen: pagehide while open", { category: "chapter-scene", level: "warning" });
  }
  function onVisibilityChange() {
    addBreadcrumb(`chapter fullscreen: visibilitychange (${document.visibilityState})`, { category: "chapter-scene" });
  }
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChange);
  return () => {
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    setSessionFlag(false);
    addBreadcrumb("chapter fullscreen: closed", { category: "chapter-scene" });
  };
}
